import React, { useMemo, useState } from "react";
import { getUnavailabilityOn, localIsoDate } from "../utils/availability";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAYS = ["D", "L", "M", "M", "J", "V", "S"];

const CELL = {
  ON_LEAVE: "bg-amber-400 text-amber-950",
  FULL: "bg-red-500 text-white",
};

const monthDays = (year, month) => {
  const n = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(year, month, i + 1);
    return { iso: localIsoDate(d), day: i + 1, dow: d.getDay() };
  });
};

const cellFor = (emp, iso) => {
  const p = getUnavailabilityOn(emp, iso);
  if (p) return { key: p.type, label: p.type === "FULL" ? "F" : "C", title: p.type === "FULL" ? "FULL" : "EN CONGÉS" };
  return null;
};

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-[11px] text-gray-600 mt-2">
    <span className="flex items-center gap-1"><span className={`w-4 h-4 rounded ${CELL.ON_LEAVE}`} /> EN CONGÉS</span>
    <span className="flex items-center gap-1"><span className={`w-4 h-4 rounded ${CELL.FULL}`} /> FULL</span>
  </div>
);

/**
 * Calendrier mensuel des absences (congés / FULL) de toute l'équipe : une ligne par employé, une colonne par jour.
 */
export const AbsenceCalendar = ({ employees }) => {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const days = useMemo(() => monthDays(ym.y, ym.m), [ym]);
  const today = localIsoDate();

  const shift = (delta) => setYm(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  return (
    <div className="border rounded-xl shadow-sm bg-white p-3 mb-3" data-testid="absence-calendar">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => shift(-1)} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" data-testid="absence-calendar-prev">◀</button>
        <span className="font-bold text-gray-800" data-testid="absence-calendar-month">{MONTHS[ym.m]} {ym.y}</span>
        <button onClick={() => shift(1)} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200" data-testid="absence-calendar-next">▶</button>
      </div>
      <div className="overflow-x-auto">
        <table className="text-[10px] border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white z-10 text-left pr-2 font-semibold text-gray-700">Employé</th>
              {days.map((d) => (
                <th key={d.iso} className={`w-5 min-w-[20px] text-center font-medium ${d.dow === 0 || d.dow === 6 ? "text-gray-400" : "text-gray-600"} ${d.iso === today ? "bg-blue-100 rounded" : ""}`}>
                  <div>{DAYS[d.dow]}</div>
                  <div>{d.day}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id} className="border-t border-gray-100" data-testid={`absence-row-${emp.id}`}>
                <td className="sticky left-0 bg-white z-10 pr-2 py-0.5 font-medium text-gray-800 whitespace-nowrap max-w-[90px] truncate" title={`${emp.prenom} ${emp.nom || ""}`}>{emp.prenom} {emp.nom || ""}</td>
                {days.map((d) => {
                  const c = cellFor(emp, d.iso);
                  return (
                    <td key={d.iso} className={`px-px py-0.5 ${d.dow === 0 || d.dow === 6 ? "bg-gray-50" : ""}`}>
                      <div
                        className={`h-5 rounded flex items-center justify-center font-bold ${c ? CELL[c.key] : ""}`}
                        title={c ? `${emp.prenom} — ${c.title}` : ""}
                        data-testid={c ? `absence-cell-${emp.id}-${d.iso}` : undefined}
                      >
                        {c?.label || ""}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Legend />
    </div>
  );
};

export default AbsenceCalendar;
